import { AgentListItem } from "@/types/agent";
import { apiConfig } from "@/lib/env";

/**
 * Fetches the list of available local chat agents from the API
 */
export const getLocalAgents = async (): Promise<AgentListItem[]> => {
  try {
    console.log("Fetching local agents from:", `${apiConfig.baseUrl}/agents/`);
    const response = await fetch(`${apiConfig.baseUrl}/agents/`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch agents: ${response.status}`);
    }
    
    const agents = await response.json();
    console.log("Local agents fetched:", agents);
    
    if (!Array.isArray(agents)) {
      console.error("Response is not an array:", agents);
      return [];
    }
    
    // Since the API already returns the full agent config including LLM data,
    // we don't need to fetch individual configs
    const agentsWithLLM = agents.map((agent: any) => ({
      ...agent,
      source: "local" as const,
      llm: agent.llm ? {
        provider: agent.llm.provider,
        model: agent.llm.model,
      } : {
        provider: "local",
        model: "default",
      },
    }));
    
    console.log("Processed agents with LLM:", agentsWithLLM);
    console.log("Sample agent LLM data:", agentsWithLLM[0]?.llm);
    return agentsWithLLM;
  } catch (error) {
    console.error("Error fetching local agents:", error);
    
    // Return fallback agents in case of an error
    return [
      {
        id: "agent-001",
        name: "Personal Assistant",
        role: "General Assistant",
        goal: "Assist with general tasks and information retrieval",
        active: true,
        source: "local" as const,
        llm: {
          provider: "local",
          model: "default",
        },
      }
    ];
  }
};

/**
 * Get a specific local agent by ID
 */
export const getLocalAgentById = async (agentId: string): Promise<AgentListItem | null> => {
  try {
    if (!agentId) return null;
    
    // First try to get from the server (local agents) using correct API URL
    const response = await fetch(`${apiConfig.baseUrl}/agents/${agentId}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch agent: ${response.status}`);
    }
    
    const agent = await response.json();
    if (agent) {
      return {
        ...agent,
        source: "local" as const,
        llm: agent.llm ? {
          provider: agent.llm.provider,
          model: agent.llm.model,
        } : undefined,
      };
    }
    
    // If not found, return a fallback agent
    console.warn(`Agent with ID ${agentId} not found, using fallback`);
    return {
      id: agentId,
      name: "AI Assistant", 
      role: "General Assistant",
      goal: "Assist with general tasks and information retrieval",
      active: true,
      source: "local" as const,
    };
  } catch (error) {
    console.error("Error fetching local agent by ID:", error);
    
    // Return a fallback with the requested ID
    return {
      id: agentId,
      name: "AI Assistant",
      role: "General Assistant", 
      goal: "Assist with general tasks and information retrieval",
      active: true,
      source: "local" as const,
    };
  }
};